import { useState } from 'react';
import './ColorRadioGroup.scss';
import { Modal, Button, Radio, RadioChangeEvent, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { SpaceLabel } from '../../types/TaskLabel';
import { EditableInput } from '../UI/EditableInput';
import { addLabel, deleteLabel, updateLabel, updateLabelsOrder } from '../../api/workspace';
import { useGetActualSpaceId, useGetWorkspace } from '../../serverState/workspace';
import { queryClient } from '../../queryClient';
import Task from '../../types/Task';
import { useGetTasks } from '../../serverState/tasks';
import Workspace from '../../types/Workspace';
import { flushSync } from 'react-dom';
import { BaseButton } from '../UI/BaseButton';
import { capitalizeFirstLetter } from '../../common/utils';
import LabelChip, { LabelChipType } from '../UI/LabelChip';
import { TaskStatusGroup } from '../../types/TaskStatus';
import StatusLabel, { StatusComponentType } from '../UI/StatusLabel';
import { StatusColor } from '../../constants/design_system/statusColors';
import { LabelsGroup } from './components/LabelsGroup';

/**
 * Label-management UI, extracted from `EditLabelsModal` so the same logic renders
 * both inside the standalone modal and the Space Settings "Labels" tab (#1120).
 * Contains no outer modal chrome — the caller provides it (or a settings panel).
 * Operates on the active route workspace, matching the standalone modal.
 */
export const EditLabelsModalContent = () => {
  const workspaceQuery = useGetWorkspace();
  const labelOptions: SpaceLabel[] = (workspaceQuery.data?.Labels as SpaceLabel[]) || [];
  const tasksQuery = useGetTasks(Number(workspaceQuery.data?.id));

  const [labels, setLabels] = useState<SpaceLabel[]>(labelOptions.sort((a, b) => a.orderIndex - b.orderIndex));
  const [selectedLabel, setSelectedLabel] = useState<SpaceLabel | null>(labels[0] || null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const actualSpaceId = useGetActualSpaceId();
  const labelHasTasks =
    tasksQuery.data && selectedLabel
      ? tasksQuery.data.some((task) => task.TaskLabels?.some((tl) => tl.labelId === selectedLabel.id))
      : false;

  const addNewLabel = async () => {
    const newLabel: SpaceLabel = {
      id: Date.now(), // Temporary ID
      title: generateDefaultName(),
      color: StatusColor.grey,
      workspaceId: Number(actualSpaceId),
      orderIndex: 0, // Position on top
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      // Shift existing labels down to make room at the top
      const labelsWithUpdatedOrder = labels.map((label, index) => ({
        ...label,
        orderIndex: index + 1,
      }));

      const createdLabelResponse = await addLabel(Number(actualSpaceId), {
        title: newLabel.title,
        color: newLabel.color,
        orderIndex: newLabel.orderIndex,
      });

      const createdLabel = createdLabelResponse.data;
      const updatedLabels = [createdLabel, ...labelsWithUpdatedOrder].sort((a, b) => a.orderIndex - b.orderIndex);

      setLabels(updatedLabels);
      setSelectedLabel(createdLabel);

      // Update workspace cache
      queryClient.setQueryData(['workspace', Number(actualSpaceId)], (oldData: Workspace) => {
        const newData = { ...oldData };
        newData.Labels = updatedLabels;
        return newData;
      });

      // Update the order indices in the backend
      try {
        const labelsOrderMapping: Record<number, number> = updatedLabels.reduce(
          (acc, label) => {
            acc[label.id] = label.orderIndex ?? 0;
            return acc;
          },
          {} as Record<number, number>
        );
        await updateLabelsOrder(Number(actualSpaceId), { labelsOrder: labelsOrderMapping });
      } catch (error: any) {
        // Ignore error
      }

      // Focus and select text of new label by clicking the title to trigger edit mode
      setTimeout(() => {
        flushSync(() => {
          (document.querySelector(`[title-id='${createdLabel.title}']`) as HTMLElement)?.click();
        });
        const newStatusInput = document.querySelector(`[input-id='${createdLabel.title}']`) as HTMLInputElement;
        newStatusInput?.focus();
        newStatusInput?.select();
      }, 200);
    } catch (error: any) {
      void messageApi.error(error.response?.data?.message || 'Failed to create label');
    }
  };

  const generateDefaultName = () => {
    const baseName = 'New Label';
    let newName = baseName;
    let counter = 1;

    const titles = labels.map((label) => label.title);

    while (titles.includes(newName)) {
      counter += 1;
      newName = `${baseName} ${counter}`;
    }

    return newName;
  };

  const selectLabel = (label: SpaceLabel) => {
    setSelectedLabel(label);
  };

  const showDeleteModal = async () => {
    setIsDeleteModalVisible(true);
  };

  const deleteSelectedLabel = async () => {
    if (!selectedLabel) return;

    try {
      await deleteLabel(Number(actualSpaceId), selectedLabel.id);
    } catch (error: any) {
      void messageApi.error(error.response?.data?.message || 'Failed to delete label');
      return;
    }

    const updatedLabels = labels.filter((label) => label.id !== selectedLabel.id);
    setLabels(updatedLabels);
    setSelectedLabel(updatedLabels[updatedLabels.length - 1] || null);

    // Update workspace cache
    queryClient.setQueryData(['workspace', Number(actualSpaceId)], (oldData: Workspace) => {
      const newData = { ...oldData };
      newData.Labels = updatedLabels;
      return newData;
    });

    // Update tasks cache to remove deleted label references
    queryClient.setQueryData(['tasks', Number(actualSpaceId)], (oldData: Task[]) => {
      return oldData.map((task) => ({
        ...task,
        TaskLabels: task.TaskLabels?.filter((tl) => tl.labelId !== selectedLabel.id) || [],
      }));
    });

    setIsDeleteModalVisible(false);
  };

  const onLabelTitleChange = async (title: string) => {
    if (!selectedLabel || selectedLabel.title === title) return;
    await updateSelectedLabel({ title });
  };

  const onColorChange = async ({ target: { value } }: RadioChangeEvent) => {
    if (!selectedLabel) return;
    await updateSelectedLabel({ color: value });
  };

  const updateSelectedLabel = async (updates: Partial<SpaceLabel>) => {
    if (!selectedLabel) return;
    const oldLabels = labels;
    const oldSelectedLabel = selectedLabel;

    const updatedLabel = { ...selectedLabel, ...updates };
    const updatedLabels = labels.map((label) => (label.id === selectedLabel.id ? updatedLabel : label));

    setLabels(updatedLabels);
    setSelectedLabel(updatedLabel);

    try {
      await updateLabel(Number(actualSpaceId), selectedLabel.id, updates);

      // Update workspace cache
      queryClient.setQueryData(['workspace', Number(actualSpaceId)], (oldData: Workspace) => {
        const newData = { ...oldData };
        newData.Labels = updatedLabels;
        return newData;
      });
    } catch (error: any) {
      // Roll back optimistic update on backend error (e.g., duplicate name)
      setLabels(oldLabels);
      setSelectedLabel(oldSelectedLabel);
      void messageApi.error(error.response?.data?.message || 'Failed to update label');
    }
  };

  const updateLabels = async (newLabels: SpaceLabel[]) => {
    let updatedSelectedLabel = selectedLabel;
    const updatedLabels = labels.map((label) => {
      const updatedLabel = newLabels.find((l) => l.id === label.id);
      if (updatedLabel && updatedLabel.id === selectedLabel?.id) {
        updatedSelectedLabel = updatedLabel;
      }
      return updatedLabel ?? label;
    });

    setLabels(updatedLabels);
    if (updatedSelectedLabel) {
      setSelectedLabel(updatedSelectedLabel);
    }

    // Update workspace cache
    queryClient.setQueryData(['workspace', Number(actualSpaceId)], (oldData: Workspace) => {
      const newData = { ...oldData };
      newData.Labels = updatedLabels;
      return newData;
    });

    try {
      const labelsOrderMapping: Record<number, number> = updatedLabels.reduce(
        (acc, label) => {
          acc[label.id] = label.orderIndex ?? 0;
          return acc;
        },
        {} as Record<number, number>
      );
      await updateLabelsOrder(Number(actualSpaceId), { labelsOrder: labelsOrderMapping });
    } catch (error: any) {
      void messageApi.error(error.response?.data?.message || 'Failed to update labels order');
      return;
    }
  };

  const handleDeleteModalCancel = () => {
    setIsDeleteModalVisible(false);
  };

  const customDeleteModalTitle = selectedLabel ? (
    <div className="h-6 justify-start items-center gap-2 inline-flex">
      <div className="text-[#313237] text-base font-semibold font-['Figtree'] leading-normal">Delete</div>
      <LabelChip label={selectedLabel} type={LabelChipType.Modal} />
      <div className="text-[#313237] text-base font-semibold font-['Figtree'] leading-normal">label?</div>
    </div>
  ) : null;

  const customDeleteModalFooter = (
    <div className="h-8 pr-3 py-3 bg-white rounded-bl-xl rounded-br-xl border-l border-r border-b border-[#ecedf5] justify-end items-start gap-3 inline-flex">
      <div
        onClick={handleDeleteModalCancel}
        className="px-3 py-2.5 bg-white rounded-lg shadow border border-[#ecedf5] justify-end items-center gap-2 inline-flex cursor-pointer"
      >
        <div className="text-[#707691] text-base font-semibold font-['Figtree'] leading-tight">Cancel</div>
      </div>
      <div
        onClick={deleteSelectedLabel}
        className="px-3 py-2.5 bg-[#e34935] rounded-lg shadow justify-end items-center gap-2 inline-flex cursor-pointer"
      >
        <div className="text-white text-base font-semibold font-['Figtree'] leading-tight">Delete Label</div>
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      {labels.length === 0 ? (
        <div className="h-[420px] flex items-center justify-center">
          <div className="h-[164px] w-[250px] flex flex-col items-center justify-center bg-neutral-100 rounded-lg">
            <div className="text-neutral-900 text-sm font-medium leading-tight mb-4">No labels yet</div>
            <BaseButton type="secondary" size="medium" onClick={addNewLabel}>
              <PlusOutlined />
              Add Label
            </BaseButton>
          </div>
        </div>
      ) : (
        <div className="h-[420px] flex">
          <div className="flex flex-col pr-4 group" style={{ flex: 1 }}>
            <div className="w-full h-6 pb-0.5 relative flex items-center mb-2 shrink-0">
              <div className="ml-1 text-neutral-900 text-xs font-semibold leading-none">Labels</div>
              <Button
                onClick={addNewLabel}
                className="h-6 w-6 p-0 rounded border flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200 group/button"
              >
                <PlusOutlined className="w-4 h-4 text-neutral-900 group-hover/button:text-brand-taskonio-green" />
              </Button>
            </div>

            <div className="overflow-y-auto">
              <LabelsGroup
                labels={labels}
                selectedLabel={selectedLabel}
                selectLabel={selectLabel}
                updateLabels={updateLabels}
              />
            </div>
          </div>

          <div className="w-px min-h-full bg-neutral-300 rounded-sm" />

          <div className="mx-4 flex flex-col" style={{ flex: 1.4 }}>
            {selectedLabel ? (
              <>
                <EditableInput value={selectedLabel.title} onValueUpdated={onLabelTitleChange} />

                <div className="text-neutral-900 text-xs font-semibold leading-none mt-4">Color</div>
                {/* Checked colour comes from ColorRadioGroup.scss, not an AntD theme
                    token — see that file for why the token could not work. #2639 */}
                <Radio.Group
                  className="color-radio-group grid grid-cols-2 gap-2 mt-2"
                  value={selectedLabel.color}
                  onChange={onColorChange}
                >
                  {Object.values(StatusColor).map((color) => (
                    <Radio className="py-1" value={color} key={color}>
                      <StatusLabel
                        color={color}
                        title={capitalizeFirstLetter(color)}
                        group={TaskStatusGroup.notStarted}
                        component={StatusComponentType.EditStatusesModalColor}
                      />
                    </Radio>
                  ))}
                </Radio.Group>

                <div className="flex justify-end mt-auto">
                  <BaseButton type="secondary" size="medium" danger={true} onClick={showDeleteModal}>
                    <DeleteOutlined />
                    Delete
                  </BaseButton>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-neutral-400"
                  >
                    <path
                      d="M12 2L14.09 8.26L20 8.26L15 12L17.18 18.74L12 15L6.82 18.74L9 12L4 8.26L9.91 8.26L12 2Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="text-neutral-900 text-base font-semibold font-['Figtree'] leading-normal mb-2">
                  Select a label
                </div>
                <div className="text-neutral-700 text-sm font-normal font-['Figtree'] leading-tight text-center max-w-[180px]">
                  Choose a label from the list to edit its name and color
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        title={customDeleteModalTitle}
        open={isDeleteModalVisible}
        onCancel={handleDeleteModalCancel}
        zIndex={2000}
        width={520}
        footer={customDeleteModalFooter}
        centered
      >
        <div className="py-4">
          {labelHasTasks ? (
            <div className="text-neutral-900">
              This label is being used by tasks. Deleting it will remove the label from all tasks.
            </div>
          ) : (
            <div className="text-neutral-900">Are you sure you want to delete this label?</div>
          )}
        </div>
      </Modal>
    </>
  );
};
